import { authConfig } from "../auth/authConfig";
import { getSsoBootstrapToken } from "../auth/ssoAuth";
// Thrown from plain functions, not components — hence the i18next singleton
// rather than the useTranslation hook.
import { i18n } from "../../i18n";

// Client for the backend's subscription endpoints. Sends the Office SSO
// bootstrap token; the backend performs the OBO exchange and manages the Graph
// subscription. Work/school accounts only — personal accounts can't do OBO.

export class CalendarAutomationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "CalendarAutomationError";
  }
}

/** What the backend reports after tearing down automation. */
export interface DisableResult {
  cancelledMessages?: number;
  meetings?: number;
}

async function callBackend(path: string): Promise<DisableResult> {
  if (!authConfig.backendUrl) {
    throw new CalendarAutomationError(i18n.t("errors.noBackend"));
  }
  let ssoToken: string;
  try {
    ssoToken = await getSsoBootstrapToken();
  } catch (err) {
    // Don't guess: 13003/13012 really are account/platform limits, but any other
    // Office error (a manifest or registration problem, say) would be hidden by
    // that message and send us looking in the wrong place.
    const code = (err as { code?: number }).code;
    // eslint-disable-next-line no-console
    console.warn("[lox24-calendar] Office SSO failed", { code, err });
    throw new CalendarAutomationError(
      code === 13003 || code === 13012
        ? i18n.t("errors.workAccountRequired")
        : i18n.t("errors.signInFailed", { code: code ? ` (${code})` : "" })
    );
  }

  const res = await fetch(`${authConfig.backendUrl}/api/${path}`, {
    method: "POST",
    headers: { Authorization: `Bearer ${ssoToken}` },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { message?: string; error?: string };
    throw new CalendarAutomationError(
      body.message ?? body.error ?? i18n.t("errors.requestFailed", { status: res.status })
    );
  }
  return (await res.json().catch(() => ({}))) as DisableResult;
}

/** How background sending is going, for the status line in Settings. */
export interface AutomationStatus {
  state: "idle" | "ok" | "problem";
  /** Stable code for what happened, translated here. See SendReason. */
  reason?: string;
  /** English diagnostic. Only shown for rows written before `reason` existed. */
  detail?: string;
  /** When the last reminder was processed. */
  at?: string;
  lastSuccessAt?: string;
  failureCount?: number;
}

/**
 * Fetch the last automation result. Returns undefined rather than throwing —
 * a status line that can't load should stay quiet, not raise an error over a
 * feature that is itself only informational.
 */
export async function getAutomationStatus(): Promise<AutomationStatus | undefined> {
  if (!authConfig.backendUrl) return undefined;
  try {
    const ssoToken = await getSsoBootstrapToken();
    const res = await fetch(`${authConfig.backendUrl}/api/automation/status`, {
      headers: { Authorization: `Bearer ${ssoToken}` },
    });
    if (!res.ok) return undefined;
    return (await res.json()) as AutomationStatus;
  } catch {
    return undefined;
  }
}

/** Enable server-side calendar SMS for the signed-in user (subscriptions/ensure). */
export const enableCalendarAutomation = (): Promise<DisableResult> =>
  callBackend("subscriptions/ensure");

/** Disable it, cancel queued reminders and purge server state. */
export const disableCalendarAutomation = (): Promise<DisableResult> =>
  callBackend("subscriptions/disable");
